import { useQueries, UseQueryResult } from "@tanstack/react-query";
import zipObject from "lodash/zipObject";
import { getOriginalResource } from "PFApp/booking/parts/helpers";
import { useCalendarGridScope } from "PFApp/booking/parts/hooks";
import { useMaxPastFetchesCount } from "PFApp/booking/parts/hooks/use_max_past_fetches_count";
import { fetchBookings } from "PFApp/booking/services/api";
import { getBookingsLevelChildrenMeta } from "PFApp/booking/store/provider/children_rows";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { Row, RowType } from "PFApp/booking/types/row";
import { isProjects, isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { utc } from "PFCore/helpers/date";
import { JsonValue, stringify } from "PFCore/helpers/use_deterministic_stringify";
import { profileKeys } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking, PaginatedCollection } from "PFTypes";
import { useEffect, useMemo } from "react";

import { ChildrenDataByRowKey } from "./use_rows_children";
import { useRowsChildrenInvalidate } from "./use_rows_children_invalidate";

type UseChildrenInPast = {
  calendarRoot: CalendarRoot;
  rows: Row[];
  childrenInPastByRowKey: Record<string, { enabled: boolean; hasChildrenInPast?: boolean }>;
  childrenDataByRowKey: ChildrenDataByRowKey;
  pastFetchesCount: number;
};
type UseChildrenInPastReturn = {
  fetchingByRowKey: Record<string, boolean>;
  hasChildrenInPastByRowKey: Record<string, boolean>;
};

export const useChildrenInPast = ({
  calendarRoot,
  rows,
  childrenInPastByRowKey,
  childrenDataByRowKey,
  pastFetchesCount
}: UseChildrenInPast): UseChildrenInPastReturn => {
  const dateRange = useCalendarGridScope({ pastFetchesCount });

  const { maxPastFetchesCount, rangeMonthsDuration } = useMaxPastFetchesCount();
  const { formatDatabaseDateTime } = useDateFormatter();

  const { invalidateAll: invalidateAllChildren } = useRowsChildrenInvalidate();

  useEffect(() => {
    invalidateAllChildren();
  }, [pastFetchesCount]);

  const pastRangeMin = useMemo(
    () =>
      formatDatabaseDateTime(
        utc(dateRange.min)
          .startOf("day")
          .subtract((maxPastFetchesCount - pastFetchesCount) * rangeMonthsDuration, "months")
      ),
    [maxPastFetchesCount, pastFetchesCount, dateRange.min]
  );
  const pastRangeMax = dateRange.min;

  const pastDateRange = {
    min: pastRangeMin,
    max: pastRangeMax
  };

  const rowsToCheck = useMemo(
    () =>
      rows.filter((row) => {
        const isEnabled = !!childrenInPastByRowKey[row.key]?.enabled;
        if (!isEnabled) {
          return false;
        }
        const childrenTotal = getBookingsLevelChildrenMeta(calendarRoot, row, childrenDataByRowKey)?.total;
        if (childrenTotal && childrenTotal > 0) {
          return false;
        }
        if (isWorkforceProfiles(calendarRoot)) {
          return row.type === RowType.Profile;
        } else if (isProjects(calendarRoot)) {
          return [RowType.Engagement, RowType.Role].includes(row.type);
        } else {
          return false;
        }
      }),
    [stringify({ calendarRoot, childrenDataByRowKey, childrenInPastByRowKey, rows } as JsonValue)]
  );

  const queriesConfig = rowsToCheck.map((row) => {
    const originalResourceId = getOriginalResource(row, calendarRoot)?.id;
    const params = {
      ...(row.type === RowType.Profile ? { profileId: originalResourceId } : {}),
      ...([RowType.Engagement, RowType.Role].includes(row.type) ? { activityId: originalResourceId } : {}),
      dateRange: pastDateRange,
      perPage: 0
    };
    return {
      queryKey: profileKeys.bookings(params.profileId || 0, params),
      queryFn: () => fetchBookings(params),
      enabled: pastFetchesCount < maxPastFetchesCount
    };
  });

  const queries: UseQueryResult<PaginatedCollection<Omit<Booking, "activity">[]>>[] = useQueries({
    queries: queriesConfig
  });

  const { fetchingByRowKey, hasChildrenInPastByRowKey } = useMemo(() => {
    const rowKeys = rowsToCheck.map(({ key }) => key);

    const fetchingByRowKey = zipObject(
      rowKeys,
      queries.map(({ isFetching }) => isFetching)
    );
    const hasChildrenInPastByRowKey = zipObject(
      rowKeys,
      queries.map(({ data }) => !!data && data.meta.total > 0)
    );

    return { fetchingByRowKey, hasChildrenInPastByRowKey };
  }, [rowsToCheck, stringify({ queries } as unknown as JsonValue)]);

  return {
    fetchingByRowKey,
    hasChildrenInPastByRowKey
  };
};
